import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { Button, Collapse, Col, message, Row, Select } from 'antd';
import { CollapseExpandIcon, CustomDivider, CustomSelect } from '../../../custom_shared/components';
import type { SectionMatchingModel } from '../../models';
import { ContractComparisonChangeLabel, ContractComparisonPanelHeader } from '..';
import SectionDetailsPopover from '../SectionDetailsPopover/SectionDetailsPopover';
import './SectionMatchingPanel.less';
import '../ContractComparisonColumns.less';

interface Props {
    matching: SectionMatchingModel;
}

const panelKey = 'section-matches';
const noMatchValue = 0;

const SectionMatchingPanel: React.FC<Props> = ({ matching }) => {
    const confirmMatches = async () => {
        if ((await matching.confirm()) === false) {
            message.error('Failed to confirm section matches');
        }
    };

    const matches = matching.draftMatches ?? matching.confirmedMatches ?? [];

    const occupiedIds = matches.flatMap(match =>
        match.previousSectionObjectId === null ? [] : [match.previousSectionObjectId]
    );

    const unmatchedPreviousSections = matching.previousSections.filter(
        section => !occupiedIds.includes(section.objectId)
    );

    const showConfirmedStatus = matching.isConfirmed && !matching.isEditing;

    return (
        <Collapse
            className="section-matching-panel"
            bordered={false}
            expandIcon={CollapseExpandIcon}
            activeKey={matching.expanded ? [panelKey] : []}
            onChange={keys => matching.setExpanded(keys.includes(panelKey))}
        >
            <Collapse.Panel
                key={panelKey}
                className="contract-comparison-panel"
                header={
                    <ContractComparisonPanelHeader
                        panelName="Match child sections"
                        changeSummaryLabel={matching.isConfirmed ? '' : 'Confirmation required'}
                    />
                }
            >
                <CustomDivider type="horizontal" />

                <div className="section-matching-content">
                    <Row className="contract-comparison-header-row contract-comparison-columns">
                        <Col>
                            <span className="header-row-text">Previous Contract · SICS</span>
                        </Col>
                        <Col>
                            <span className="header-row-text">New Contract</span>
                        </Col>
                        <Col className="contract-comparison-status" />
                    </Row>

                    <CustomDivider type="horizontal" />

                    {!matching.newSections.length && (
                        <p className="section-matching-note section-matching-introduction">
                            There are no child sections in the new contract.
                        </p>
                    )}

                    {matching.newSections.map((section, index) => {
                        const previousId = matches.find(
                            match => match.newSectionId === section.id
                        )?.previousSectionObjectId;

                        return (
                            <React.Fragment key={section.id}>
                                {index > 0 && <CustomDivider type="horizontal" />}

                                <Row className="section-matching-row contract-comparison-columns">
                                    <Col>
                                        <CustomSelect
                                            aria-label={`Previous section for ${section.name}`}
                                            label="Previous section"
                                            value={previousId ?? noMatchValue}
                                            disabled={matching.isConfirming}
                                            onChange={(value: string | number) =>
                                                matching.setMatch(
                                                    section.id,
                                                    value === noMatchValue ? null : String(value)
                                                )
                                            }
                                        >
                                            <Select.Option value={noMatchValue}>
                                                No matching previous section
                                            </Select.Option>
                                            {matching.previousSections.map(previous => (
                                                <Select.Option
                                                    key={previous.objectId}
                                                    value={previous.objectId}
                                                    title={previous.name}
                                                    disabled={
                                                        previous.objectId !== previousId &&
                                                        occupiedIds.includes(previous.objectId)
                                                    }
                                                >
                                                    {previous.name}
                                                </Select.Option>
                                            ))}
                                        </CustomSelect>
                                    </Col>

                                    <Col>
                                        <div className="section-matching-new-section">
                                            <div className="section-matching-name" title={section.name}>
                                                <span>{section.name}</span>
                                            </div>
                                            <SectionDetailsPopover
                                                sectionName={section.name}
                                                description={section.description}
                                                getDetails={() => matching.getSectionDetails(section.id)}
                                            />
                                        </div>
                                    </Col>

                                    <Col className="contract-comparison-status">
                                        {showConfirmedStatus && (
                                            <span className={previousId ? 'section-matched' : 'section-unmatched'}>
                                                {previousId ? 'Matched' : 'Unmatched'}
                                            </span>
                                        )}
                                    </Col>
                                </Row>
                            </React.Fragment>
                        );
                    })}

                    {unmatchedPreviousSections.map((section, index) => (
                        <React.Fragment key={`previous:${section.objectId}`}>
                            {(matching.newSections.length > 0 || index > 0) && <CustomDivider type="horizontal" />}

                            <Row className="section-matching-row contract-comparison-columns">
                                <Col>
                                    <CustomSelect label="Previous section" value={section.objectId} disabled>
                                        <Select.Option value={section.objectId} title={section.name}>
                                            {section.name}
                                        </Select.Option>
                                    </CustomSelect>
                                </Col>

                                <Col>
                                    <div className="section-matching-new-section">
                                        <span>No matching new section</span>
                                    </div>
                                </Col>

                                <Col className="contract-comparison-status">
                                    {showConfirmedStatus && <ContractComparisonChangeLabel changeType="removed" />}
                                </Col>
                            </Row>
                        </React.Fragment>
                    ))}

                    {matching.isEditing && (
                        <div className="section-matching-actions">
                            <span className="section-matching-note">
                                Only matched sections are compared field by field.
                            </span>

                            {matching.isConfirmed && (
                                <Button disabled={matching.isConfirming} onClick={() => matching.cancelEdit()}>
                                    Discard
                                </Button>
                            )}

                            <Button
                                type="primary"
                                loading={matching.isConfirming}
                                disabled={!matching.canConfirm}
                                onClick={confirmMatches}
                            >
                                {matching.isConfirmed ? 'Apply matches' : 'Confirm matches'}
                            </Button>
                        </div>
                    )}
                </div>
            </Collapse.Panel>
        </Collapse>
    );
};

export default observer(SectionMatchingPanel);
